<template>
    <div class="flex flex-col bg-white" style="min-height: 560px">

        <div class="flex-1 overflow-y-auto">

            <!-- CRÉNEAUX -->
            <div v-show="currentStepId === 'slots'" class="h-full flex flex-col">
                <div class="md:px-6 py-4">
                    <h2 class="text-lg font-bold text-gray-900">Quand souhaitez-vous intervenir ?</h2>
                    <p class="text-xs text-gray-400 mt-0.5">Cliquez sur les demi-journées souhaitées dans le planning</p>
                </div>
                <div class="md:mx-6 mb-4 border border-gray-200 rounded-xl overflow-hidden flex-1">
                    <planning-half-day
                        :multi-slots="true"
                        :days="openDays"
                        :heure-debut-matin="heureDebutMatin"
                        :heurefin-matin="heurefinMatin"
                        :heure-debut-apres-midi="heureDebutApresMidi"
                        :heure-fin-apres-midi="heureFinApresMidi"
                        @slot-selection-change="onSlotChange"
                    />
                </div>
            </div>

            <!-- ADRESSE + CLIENT -->
            <div v-show="currentStepId === 'contact'" class="grid grid-cols-1 md:grid-cols-2 gap-0" style="min-height: 480px">
                <div class="md:px-6 py-5 flex flex-col gap-4 border-r border-gray-100 overflow-y-auto">

                    <!-- Adresse -->
                    <div>
                        <label class="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">
                            <i class="fas fa-map-marker-alt mr-1 text-orange-400"></i> Adresse
                        </label>
                        <div v-if="!adresseSelected" class="relative">
                            <input v-model="adresseQuery" type="text" placeholder="Rechercher une adresse..."
                                class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 transition-colors pr-10"
                                @input="onAdresseInput" @blur="hideAdresseSuggestionsDelayed" />
                            <i v-if="adresseLoading" class="fas fa-spinner fa-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                            <div v-if="showAdresseSuggestions && adresseSuggestions.length"
                                class="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                <div v-for="(item, i) in adresseSuggestions" :key="i"
                                    @mousedown.prevent="selectAdresse(item)"
                                    class="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-orange-50 border-b border-gray-50 last:border-0 text-sm">
                                    <i class="fas fa-map-marker-alt text-orange-300 text-xs flex-shrink-0"></i>
                                    {{ item._label }}
                                </div>
                            </div>
                        </div>
                        <div v-else class="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <div class="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <i class="fas fa-map-marker-alt text-orange-500 text-xs"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-semibold text-gray-900 truncate">{{ adresseSelected.adresse1 }}</p>
                                <p class="text-xs text-gray-400">{{ adresseSelected.code_postal }} {{ adresseSelected.ville }}</p>
                            </div>
                            <button @click="resetAdresse" class="text-xs text-gray-400 hover:text-orange-500 font-medium">Modifier</button>
                        </div>
                    </div>

                    <!-- Client (propriétaire) -->
                    <div v-if="adresseSelected" class="flex flex-col gap-3">
                        <label class="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <i class="fas fa-user mr-1 text-orange-400"></i> Propriétaire / Client facturé
                        </label>
                        <div v-if="selectedClient" class="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <div class="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-orange-600">{{ clientInitials(selectedClient) }}</div>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-semibold text-gray-900 truncate capitalize">{{ selectedClient.prenom }} {{ selectedClient.nom }}</p>
                                <p v-if="selectedClient.telephone" class="text-xs text-gray-400 truncate">{{ selectedClient.telephone }}</p>
                            </div>
                            <button @click="resetClient" class="text-xs text-gray-400 hover:text-orange-500 font-medium">Modifier</button>
                        </div>
                        <template v-else>
                            <div v-if="loadingSearchClients" class="flex items-center gap-2 text-xs text-gray-400">
                                <i class="fas fa-spinner fa-spin"></i> Recherche...
                            </div>
                            <div v-else-if="clientsFound.length > 0">
                                <div class="space-y-1.5 max-h-48 overflow-y-auto">
                                    <button v-for="client in clientsFound" :key="client.id" @click="selectClient(client)"
                                        class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all text-sm border-gray-100 hover:border-orange-300 hover:bg-orange-50 bg-white">
                                        <div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500">{{ clientInitials(client) }}</div>
                                        <div class="flex-1 min-w-0">
                                            <p class="font-semibold text-gray-900 truncate capitalize">{{ client.prenom }} {{ client.nom }}</p>
                                            <p v-if="client.telephone" class="text-xs text-gray-400 truncate">{{ client.telephone }}</p>
                                        </div>
                                        <i class="fas fa-chevron-right text-gray-300 text-xs"></i>
                                    </button>
                                </div>
                                <div class="flex items-center gap-2 pt-2 mb-2">
                                    <div class="flex-1 h-px bg-gray-100"></div>
                                    <span class="text-xs text-gray-300">ou nouveau client</span>
                                    <div class="flex-1 h-px bg-gray-100"></div>
                                </div>
                                <div class="border border-gray-200 rounded-xl overflow-hidden">
                                    <button @click="newClientOpen = !newClientOpen" class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                                        <span><i class="fas fa-plus mr-2 text-orange-400 text-xs"></i>Créer un client</span>
                                        <i :class="['fas fa-chevron-down text-gray-300 text-xs transition-transform', newClientOpen ? 'rotate-180' : '']"></i>
                                    </button>
                                    <div v-if="newClientOpen" class="border-t border-gray-100 px-4 py-3">
                                        <client-block title="" icon="fas fa-user" id-prefix="new-client-create" :value="newClientData" @input="onNewClientInput" />
                                    </div>
                                </div>
                            </div>
                            <div v-else class="border border-gray-200 rounded-xl px-4 py-3">
                                <client-block title="" icon="fas fa-user" id-prefix="new-client-create" :value="newClientData" @input="onNewClientInput" />
                            </div>
                        </template>
                    </div>

                    <!-- Régie -->
                    <div v-if="adresseSelected" class="space-y-2">
                        <div class="flex items-center justify-between">
                            <span class="text-xs text-gray-500"><i class="fas fa-building mr-1.5 text-gray-400"></i>Adresse gérée par une régie</span>
                            <div class="flex gap-1">
                                <button @click="regieOpen = true"
                                    :class="regieOpen ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-green-500'"
                                    class="text-xs font-semibold px-2.5 py-1 rounded-lg transition-all">
                                    Oui
                                </button>
                                <button @click="regieOpen = false; selectedRegie = null; newRegieData = emptyClientData({ type: 'regie', type_client: 'professionnel' })"
                                    :class="!regieOpen ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-red-500'"
                                    class="text-xs font-semibold px-2.5 py-1 rounded-lg transition-all">
                                    Non
                                </button>
                            </div>
                        </div>
                        <div v-if="regieOpen" class="border border-gray-200 rounded-xl p-3">
                            <div v-if="selectedRegie" class="flex items-center gap-3">
                                <div class="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <i class="fas fa-building text-blue-500 text-xs"></i>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-semibold text-gray-900 truncate">{{ selectedRegie.nom }}</p>
                                    <p v-if="selectedRegie.telephone" class="text-xs text-gray-400">{{ selectedRegie.telephone }}</p>
                                </div>
                                <button @click="selectedRegie = null" class="text-xs text-gray-400 hover:text-red-500 font-medium">Retirer</button>
                            </div>
                            <template v-else>
                                <client-block
                                    title=""
                                    icon="fas fa-building"
                                    id-prefix="new-regie"
                                    :value="newRegieData"
                                    :force-pro="true"
                                    @input="onNewRegieInput"
                                    @client-selected="onRegieSelected"
                                />
                            </template>
                        </div>
                    </div>

                    <!-- Locataire -->
                    <div v-if="adresseSelected" class="space-y-2">
                        <div class="flex items-center justify-between">
                            <span class="text-xs text-gray-500"><i class="fas fa-user mr-1.5 text-gray-400"></i>Un locataire occupe cette adresse</span>
                            <div class="flex gap-1">
                                <button @click="locataireOpen = true"
                                    :class="locataireOpen ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-green-500'"
                                    class="text-xs font-semibold px-2.5 py-1 rounded-lg transition-all">
                                    Oui
                                </button>
                                <button @click="locataireOpen = false; selectedLocataire = null; newLocataireData = emptyClientData({ type: 'locataire' })"
                                    :class="!locataireOpen ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-red-500'"
                                    class="text-xs font-semibold px-2.5 py-1 rounded-lg transition-all">
                                    Non
                                </button>
                            </div>
                        </div>
                        <div v-if="locataireOpen" class="border border-gray-200 rounded-xl p-3">
                            <div v-if="selectedLocataire" class="flex items-center gap-3">
                                <div class="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-green-600">{{ clientInitials(selectedLocataire) }}</div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-semibold text-gray-900 truncate capitalize">{{ selectedLocataire.prenom }} {{ selectedLocataire.nom }}</p>
                                    <p v-if="selectedLocataire.telephone" class="text-xs text-gray-400">{{ selectedLocataire.telephone }}</p>
                                </div>
                                <button @click="selectedLocataire = null" class="text-xs text-gray-400 hover:text-red-500 font-medium">Retirer</button>
                            </div>
                            <template v-else>
                                <client-block
                                    title=""
                                    icon="fas fa-user"
                                    id-prefix="new-locataire"
                                    :value="newLocataireData"
                                    @input="onNewLocataireInput"
                                    @client-selected="onLocataireSelected"
                                />
                            </template>
                        </div>
                    </div>

                </div>

                <!-- Carte -->
                <div class="relative bg-gray-100 hidden md:block" style="min-height: 420px">
                    <div id="chantier-create-map" class="absolute inset-0"></div>
                    <div v-if="!adresseSelected" class="absolute inset-0 flex flex-col items-center justify-center text-gray-300 pointer-events-none">
                        <i class="fas fa-map-marked-alt text-5xl mb-3 opacity-30"></i>
                        <p class="text-sm">Entrez une adresse</p>
                    </div>
                </div>
            </div>

            <!-- APPAREILS & INTERVENTIONS -->
            <appareils-parc
                v-show="currentStepId === 'appareils'"
                :client-id="appareilsClientId"
                :chantier-id="chantierId || null"
                :coordonnees="appareilsCoordonnees"
                @change="onAppareilsChange"
                @device-deleted="onDeviceDeleted"
                ref="appareilsParc"
            />

        </div>

        <!-- Footer -->
        <div class="md:px-6 py-4 border-t border-gray-100 flex items-center gap-3">
            <button v-if="currentStep > 0" @click="prevStep" :disabled="loadingSubmit"
                class="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all disabled:opacity-40">
                <i class="fas fa-arrow-left text-xs"></i> Retour
            </button>
            <div class="flex-1"></div>
            <button @click="handleNext" :disabled="!canGoNext || loadingSubmit"
                class="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all"
                :class="canGoNext && !loadingSubmit ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'">
                <i v-if="loadingSubmit" class="fas fa-spinner fa-spin"></i>
                <template v-else-if="isLastStep"><i class="fas fa-paper-plane"></i><span>Planifier</span></template>
                <template v-else><span>Continuer</span><i class="fas fa-arrow-right text-xs"></i></template>
            </button>
        </div>
    </div>
</template>

<script>
import PlanningHalfDay from '../../Planning/Components/PlanningHalfDay.vue'
import ClientBlock     from '../../Client/Components/ClientBlock.vue'
import AppareilsParc  from './../../Client/Components/AppareilsParc.vue'

const GOUV_URL = 'https://api-adresse.data.gouv.fr/search/'

function emptyClientData(overrides = {}) {
    return { type: 'proprietaire', type_client: 'particulier', nom: '', prenom: '', siret: '', telephone: '', email: '', regie_id: 0, proprietaire_id: 0, ...overrides }
}

export default {
    name: 'ChantierInit',
    components: { PlanningHalfDay, ClientBlock, AppareilsParc },
    props: { chantierId: { type: Number, default: null } },

    data() {
        return {
            currentStep:  0,
            chantierData: null,
            selectedSlots: [],

            // Adresse
            adresseQuery: '', adresseSuggestions: [], adresseDebounce: null,
            adresseLoading: false, showAdresseSuggestions: false, adresseSelected: null,
            _map: null,

            // Client principal
            clientsFound: [], loadingSearchClients: false, selectedClient: null,
            newClientOpen: false,
            newClientData: emptyClientData(),

            // Régie
            regieOpen: false,
            selectedRegie: null,
            newRegieData: emptyClientData({ type: 'regie', type_client: 'professionnel' }),

            // Locataire
            locataireOpen: false,
            selectedLocataire: null,
            newLocataireData: emptyClientData({ type: 'locataire' }),

            loadingSubmit: false,
            configuredAppareils: [],

            addressesFound: [],
        }
    },

    computed: {
        hasChantier() { return !!this.chantierId },

        visibleSteps() {
            const all = [
                { id: 'slots',     label: 'Créneaux' },
                { id: 'contact',   label: 'Adresse & Client' },
                { id: 'appareils', label: 'Travaux' },
            ]
            return this.hasChantier ? [all[0], all[2]] : all
        },

        currentStepId() { return this.visibleSteps[this.currentStep]?.id },
        isLastStep()    { return this.currentStep === this.visibleSteps.length - 1 },

        canGoNext() {
            if (this.currentStepId === 'slots')     return this.selectedSlots.length > 0
            if (this.currentStepId === 'contact')   return this.adresseSelected && (this.selectedClient || !!this.newClientData.nom?.trim())
            if (this.currentStepId === 'appareils') return this.configuredAppareils.length > 0
            return false
        },

        openDays()            { return this.$account?.account?.societe?.open_days || null },
        heureDebutMatin()     { return this.$account?.account?.societe?.heure_ouverture?.slice(0, 5) || '08:00' },
        heurefinMatin()       { return '12:00' },
        heureDebutApresMidi() { return '12:01' },
        heureFinApresMidi()   { return this.$account?.account?.societe?.heure_fermeture?.slice(0, 5) || '17:00' },

        totalDuree() {
            return this.configuredAppareils.reduce((s, d) => s + (parseInt(d.duree_estimee) || 0), 0)
        },

        appareilsCoordonnees() {
            if (!this.adresseSelected?.latitude) return null
            return { latitude: this.adresseSelected.latitude, longitude: this.adresseSelected.longitude }
        },

        appareilsClientId() {
            if (this.hasChantier && this.chantierData?.client_id) return this.chantierData.client_id
            return this.selectedClient?.id || null
        },
    },

    watch: {
        currentStep(newVal, oldVal) {
            const nId = this.visibleSteps[newVal]?.id
            const oId = this.visibleSteps[oldVal]?.id
            if (oId === 'contact' && this._map) { this._map.remove(); this._map = null }
            if (nId === 'contact' && this.adresseSelected) this.$nextTick(() => this.initMap())
        },
    },

    methods: {
        /* ── Navigation ── */
        async handleNext() {
            if (!this.canGoNext || this.loadingSubmit) return
            if (this.isLastStep) { await this.submit(); return }
            if (this.currentStepId === 'contact' && this._map) { this._map.remove(); this._map = null }
            this.currentStep++
            if (this.currentStepId === 'contact' && this.adresseSelected) this.$nextTick(() => this.initMap())
        },
        prevStep()             { if (this.currentStep > 0) this.currentStep-- },
        onSlotChange(slots)    { this.selectedSlots = slots },
        onAppareilsChange(a)   { this.configuredAppareils = a },
        onDeviceDeleted({ appareil_id }) { console.log('Appareil supprimé :', appareil_id) },

        /* ── Adresse ── */
        onAdresseInput() {
            clearTimeout(this.adresseDebounce); this.adresseSuggestions = []
            if (this.adresseQuery.length < 3) return
            this.adresseDebounce = setTimeout(() => this.fetchSuggestions(), 350)
        },
        async fetchSuggestions() {
            this.adresseLoading = true
            try {
                const r = await fetch(`${GOUV_URL}?q=${encodeURIComponent(this.adresseQuery)}&limit=5`)
                const d = await r.json()
                this.adresseSuggestions = d.features.map(f => ({
                    _label:      f.properties.label,
                    adresse1:    f.properties.name,
                    code_postal: f.properties.postcode,
                    ville:       f.properties.city,
                    latitude:    f.geometry.coordinates[1],
                    longitude:   f.geometry.coordinates[0],
                }))
                this.showAdresseSuggestions = !!this.adresseSuggestions.length
            } catch (e) { console.error(e) }
            finally { this.adresseLoading = false }
        },
        selectAdresse(item) {
            this.adresseSelected = { ...item }; this.adresseQuery = item._label
            this.adresseSuggestions = []; this.showAdresseSuggestions = false
            this.selectedClient = null; this.configuredAppareils = []
            this.$nextTick(() => this.initMap()); this.loadClientsAtAddress()
        },
        resetAdresse() {
            this.adresseSelected = null; this.adresseQuery = ''; this.adresseSuggestions = []
            this.clientsFound = []; this.selectedClient = null; this.newClientOpen = false
            this.newClientData = emptyClientData()
            this.selectedRegie = null; this.newRegieData = emptyClientData({ type: 'regie', type_client: 'professionnel' }); this.regieOpen = false
            this.selectedLocataire = null; this.newLocataireData = emptyClientData({ type: 'locataire' }); this.locataireOpen = false
            this.configuredAppareils = []
            this.addressesFound = []
            if (this._map) { this._map.remove(); this._map = null }
        },
        hideAdresseSuggestionsDelayed() { setTimeout(() => { this.showAdresseSuggestions = false }, 200) },
        initMap() {
            if (!this.adresseSelected) return
            const a = this.adresseSelected
            setTimeout(() => {
                if (this._map) { this._map.remove(); this._map = null }
                if (typeof mapboxgl === 'undefined') return
                const el = document.getElementById('chantier-create-map'); if (!el) return
                mapboxgl.accessToken = 'pk.eyJ1IjoidmljdG9yYW53IiwiYSI6ImNrajhuam1iOTZ1dnQydnFqbzRldDJrdW8ifQ.VkIKVhh5_Pcp7PxqQ-GCCg'
                this._map = new mapboxgl.Map({ container: 'chantier-create-map', style: 'mapbox://styles/mapbox/streets-v12', center: [parseFloat(a.longitude), parseFloat(a.latitude)], zoom: 15 })
                new mapboxgl.Marker({ color: '#f97316' }).setLngLat([parseFloat(a.longitude), parseFloat(a.latitude)]).addTo(this._map)
            }, 200)
        },

        /* ── Client principal ── */
        async loadClientsAtAddress() {
            if (!this.adresseSelected) return
            this.loadingSearchClients = true
            try {
                const res = await this.$request.post('/clients/search-geocode', {
                    longitude: this.adresseSelected.longitude,
                    latitude:  this.adresseSelected.latitude,
                })
                this.addressesFound = res ?? []
                this.clientsFound   = this.addressesFound.map(a => a.proprietaire).filter(Boolean)
            } catch (e) { console.error(e) }
            finally { this.loadingSearchClients = false }
        },
        selectClient(client) {
            this.selectedClient  = client
            this.newClientOpen   = false
            this.newClientData   = emptyClientData()
            this.configuredAppareils = []

            const addr = this.addressesFound?.find(a => a.proprietaire?.id === client.id)

            // ← Fix : on mémorise l'adresse_id de la BDD
            if (addr) {
                this.adresseSelected = { ...this.adresseSelected, adresse_id: addr.id }
            }

            if (addr?.regie) {
                this.selectedRegie = addr.regie
                this.regieOpen     = true
            } else {
                this.selectedRegie = null
                this.regieOpen     = false
            }

            if (addr?.locataire) {
                this.selectedLocataire = addr.locataire
                this.locataireOpen     = true
            } else {
                this.selectedLocataire = null
                this.locataireOpen     = false
            }
        },
        resetClient() { this.selectedClient = null },
        clientInitials(c) { return ((c.prenom || '').charAt(0) + (c.nom || '').charAt(0)).toUpperCase() || '?' },
        onNewClientInput(val) {
            if (val.client_id) {
                this.selectedClient = { id: val.client_id, nom: val.nom || '', prenom: val.prenom || '', telephone: val.telephone || '', email: val.email || '' }
                this.newClientData = { ...val }; this.newClientOpen = false
            } else { this.newClientData = { ...val }; this.selectedClient = null }
        },

        /* ── Régie ── */
        onNewRegieInput(val) {
            this.newRegieData = { ...val, type: 'regie', type_client: 'professionnel' }
        },
        onRegieSelected(s) {
            this.selectedRegie = s
        },

        /* ── Locataire ── */
        onNewLocataireInput(val) {
            this.newLocataireData = { ...val, type: 'locataire' }
        },
        onLocataireSelected(s) {
            this.selectedLocataire = s
        },

        /* ── Payload ── */
        buildPayload() {
            const duree = this.totalDuree || 60
            const payload = {
                chantier_id: this.chantierId || null,
                appareils:   this.configuredAppareils,
                slots: this.selectedSlots.map(s => ({
                    date:         s.date,
                    slot:         s.slot,
                    tech_id:      parseInt(s.account_id),
                    duree_estimee: duree,
                })),
            }

            if (this.hasChantier && this.chantierData) {
                payload.client_id  = this.chantierData.client_id
                payload.adresse_id = this.chantierData.adresse_id
            } else {
                payload.adresse_id = this.adresseSelected?.adresse_id || null
                payload.adresse = {
                    adresse1:    this.adresseSelected?.adresse1    || null,
                    code_postal: this.adresseSelected?.code_postal || null,
                    ville:       this.adresseSelected?.ville       || null,
                    latitude:    this.adresseSelected?.latitude    || null,
                    longitude:   this.adresseSelected?.longitude   || null,

                    // Régie
                    regie_id: this.selectedRegie?.id || null,
                    regie:    !this.selectedRegie && this.newRegieData.nom
                                ? { ...this.newRegieData, type: 'regie', type_client: 'professionnel' }
                                : null,

                    // Locataire
                    locataire_id: this.selectedLocataire?.id || null,
                    locataire:    !this.selectedLocataire && this.newLocataireData.nom
                                    ? { ...this.newLocataireData, type: 'locataire' }
                                    : null,
                }

                if (this.selectedClient) {
                    payload.client_id = this.selectedClient.id; payload.client = null
                } else {
                    payload.client_id = null
                    payload.client = { ...this.newClientData }
                    delete payload.client.client_id
                }
            }

            return payload
        },

        async submit() {
            this.loadingSubmit = true
            try {
                await this.$request.post('/interventions/planifier-json', this.buildPayload())
                this.$toast.success('Chantier planifié')
                this.$emit('slots-created')
                this.resetForm()
            } catch (e) {
                console.error(e); this.$toast.error('Une erreur est survenue.')
            } finally {
                this.loadingSubmit = false
            }
        },

        resetForm() {
            this.currentStep      = 0
            this.chantierData     = null
            this.selectedSlots    = []
            this.configuredAppareils = []

            this.adresseQuery            = ''
            this.adresseSuggestions      = []
            this.adresseLoading          = false
            this.showAdresseSuggestions  = false
            if (this._map) { this._map.remove(); this._map = null }
            this.adresseSelected         = null

            this.clientsFound        = []
            this.loadingSearchClients = false
            this.selectedClient      = null
            this.newClientOpen       = false
            this.newClientData       = emptyClientData()

            this.regieOpen      = false
            this.selectedRegie  = null
            this.newRegieData   = emptyClientData({ type: 'regie', type_client: 'professionnel' })

            this.locataireOpen      = false
            this.selectedLocataire  = null
            this.newLocataireData   = emptyClientData({ type: 'locataire' })

            this.addressesFound = []

            this.$eventHub.$emit('reset-appareils-parc')
            this.$eventHub.$emit('refresh-planning')
            this.$eventHub.$emit('clear-selection')
        },
    },

    async mounted() {
        if (this.chantierId) {
            try {
                const res = await this.$request.post(`/chantiers/${this.chantierId}/view`)
                this.chantierData = res
            } catch (e) { console.error(e) }
        }
        this.$eventHub.$on('reset-chantier-init', this.resetForm)
    },

    beforeDestroy() {
        if (this._map) { this._map.remove(); this._map = null }
        clearTimeout(this.adresseDebounce)
    },
}
</script>